/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG, GMTVP, NON_TVP_FCORGS, VEHICLE_COLDEF} from '../../../common/constants';
import {hasDuplicates, range} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {FilterTvpScenarioRequestModel} from '../../../models/filter-tvp-scenario-request.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';
import {LocalBrandService} from '../../../services/local-brand.service';
import {MarketingDivisionService} from '../../../services/marketing-division.service';
import {ProgramCodeService} from '../../../services/program-code.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-vehicle-consolidation-report',
    templateUrl: './vehicle-consolidation-report.component.html',
    styleUrl: './vehicle-consolidation-report.component.scss',
    standalone: false
})
export class VehicleConsolidationReportComponent implements OnInit {
  protected readonly NON_TVP_FCORGS = NON_TVP_FCORGS;
  protected readonly DEFAULT_VEHICLE_COL_HEADERS_AGGRID = VEHICLE_COLDEF;

  industrySpinner: boolean = false;
  vehicleSpinner: boolean = false;
  propulsionSpinner: boolean = false;
  showSpinner: boolean
  scenarioSourceType: number = 0;
  selectedFcorg: string;

  startYear: number;
  endYear: number;
  yearRange: number[];

  // Checkboxes
  includeIndustry: boolean = true;
  includePropulsion: boolean = false;
  removeRecidNa: boolean = true;
  aggregateTos: boolean = true;
  includeZeroVolumes: boolean = true;
  includeLCA: boolean = false;
  showHiddenScenarios: boolean = false;
  disableIncludePropulsion: boolean = false;
  disableIncludeIndustry: boolean = false;

  // Scenario Lists
  tvpScenarios: VehicleScenario[];
  displayedTvpScenarios: VehicleScenario[];

  // Selected Scenarios:
  selectedScenarios: VehicleScenario[] = [];
  selectedGlobalScenario: any;

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false,
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  fcorgs = [];

  // Filter Lists
  forecastTypeOptions: SelectableOptions = new SelectableOptions();
  forecastStatusOptions: SelectableOptions = new SelectableOptions();
  propulsionTypeOptions: SelectableOptions = new SelectableOptions();
  engProgClassOptions: SelectableOptions = new SelectableOptions();
  forecastGateOptions: SelectableOptions = new SelectableOptions();
  architectureOptions: SelectableOptions = new SelectableOptions();
  marketDivisionOptions: SelectableOptions = new SelectableOptions();
  localBrandOptions: SelectableOptions = new SelectableOptions();
  programCodeOptions: SelectableOptions = new SelectableOptions();

  forecastTypes: any[];
  forecastStatus: any[];
  propulsionTypes: any[];
  engProgClasses: any[];
  forecastGates: any[];
  architectures: any[];
  marketDivisions: any[];
  localBrands: any[];
  programCodes: any[];


  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              readonly vehicleScenarioService: VehicleScenarioService,
              readonly industryScenarioService: IndustryScenarioService,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly marketingDivisionService: MarketingDivisionService,
              readonly programCodeService: ProgramCodeService,
              readonly localBrandService: LocalBrandService,
              public stateService: StateService) {
  }

  async ngOnInit(): Promise<void> {
    this.showSpinner = true;
    this.getYearRange();
    await this.fillLists();
    await this.getTVPScenarios();
    this.showSpinner = false;
  }

  /**
   * Fills the lists for the tvp filters
   */
  async fillLists() {
    try {
      const forecastTypePromise = this.vehicleScenarioService.getAllScenarioTypes().toPromise();
      const forecastGatePromise = this.vehicleScenarioService.getAllScenarioCategories().toPromise();
      const propTypePromise = this.vehicleScenarioService.getAllPropulsionTypes().toPromise();
      const engProgClassPromise = this.vehicleScenarioService.getAllEngProgClass().toPromise();
      const forecastStatusPromise = this.vehicleScenarioService.getAllForecastStates().toPromise();
      const marketDivPromise = this.marketingDivisionService.getAllMarketDivisions().toPromise();
      const architecturePromise = this.vehicleScenarioService.getTvpArchitectures().toPromise();
      const programCodePromise = this.programCodeService.getAllProgramCodes().toPromise();
      const localBrandPromise = this.localBrandService.getAllLocalBrands().toPromise();
      [this.forecastTypes, this.forecastGates, this.propulsionTypes, this.engProgClasses, this.forecastStatus,
        this.marketDivisions, this.architectures, this.programCodes, this.localBrands] = await Promise.all(
        [forecastTypePromise, forecastGatePromise, propTypePromise, engProgClassPromise, forecastStatusPromise,
          marketDivPromise, architecturePromise, programCodePromise, localBrandPromise]);
      this.forecastTypeOptions.options = this.forecastTypes.map(element => element.SCEN_TYPE);
      this.forecastGateOptions.options = this.forecastGates.map(element => element.scenarioCategory).reverse();
      this.propulsionTypeOptions.options = this.propulsionTypes.map(element => element.propulsionType);
      this.engProgClassOptions.options = this.engProgClasses.map(element => element.engProgClass);
      this.forecastStatusOptions.options = this.forecastStatus.map(element => element.forecastState);
      this.marketDivisionOptions.options = this.marketDivisions.map(entry => entry.marketDivision);
      this.architectureOptions.options = this.architectures.map(element => element.arch);
      this.programCodeOptions.options = this.programCodes.map(entry => entry.prgmCode);
      this.localBrandOptions.options = this.localBrands.map(entry => entry.localBrandValue);
    } catch {
      this.snackBar.displayError("Error fetching TVP list filters", DISMISS_MSG);
    }
  }

  /**
   * Fetches all TVP scenarios
   */
  async getTVPScenarios(): Promise<void> {
    try {
      this.tvpScenarios = (await this.vehicleScenarioService.getVehicleScenarios(this.stateService.getUserGmid()).toPromise())
      .filter((scenario: VehicleScenario) => scenario.fcorg === GMTVP);
      this.displayedTvpScenarios = [...this.tvpScenarios];
    } catch {
      this.snackBar.displayError('Error fetching scenarios.', DISMISS_MSG);
    }
  }

  /**
   * Sends a request to the backend to get the report(s)
   */
  getReport(): void {
    this.getVehicleConsolidationReport();
    if (this.includeIndustry) {
      this.getIndustryConsolidationReport();
    }
    if (this.includePropulsion) {
      this.getPropulsionConsolidationReport();
    }
  }

  /**
   * Gets the vehicle consolidation report
   */
  getVehicleConsolidationReport(): void {
    this.vehicleSpinner = true;
    const idList = this.selectedScenarios.map(scenario => scenario.vehScenId);
    let scenarioString: string;
    if (this.scenarioSourceType == 1) {
      scenarioString = this.selectedGlobalScenario?.id;
    } else {
      scenarioString = idList.length > 1 ? idList.sort()[0] + "-" + idList.sort()[idList.length - 1] : idList[0]?.toString();
    }
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'VehicleConsolidation_' + scenarioString + "_" + currDate;

    const aggregateTosString = this.aggregateTos ? 'yes' : 'no';
    const zeroVolumesString = this.includeZeroVolumes ? 'yes' : 'no';
    const dropRecidsString = this.removeRecidNa ? 'yes' : 'no';
    const includeLCAString = this.includeLCA ? 'yes' : 'no';
    this.vehicleScenarioService.getConsolidationReport(idList.join(", "), this.startYear, this.endYear,
      this.stateService.getUserGmid(), aggregateTosString, zeroVolumesString, dropRecidsString, includeLCAString).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting Vehicle Consolidation Report.', DISMISS_MSG);
        this.vehicleSpinner = false;
      }, () => {
        this.vehicleSpinner = false;
      }
    );
  }

  /**
   * Gets the industry consolidation report
   */
  getIndustryConsolidationReport(): void {
    this.industrySpinner = true;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    let fcorg: string;
    const isGlobal = this.scenarioSourceType === 1;
    const selectedFcorgs = [...new Set(this.selectedScenarios.map(scenario => scenario.fcorg))];
    const isMultipleFcorgs = selectedFcorgs.length > 1;
    if (isMultipleFcorgs || isGlobal || this.scenarioSourceType === 2) {
      fcorg = 'All';
    } else {
      fcorg = selectedFcorgs[0];
    }
    const idList = this.selectedScenarios.filter(scenario => !scenario.fcorg.includes("NONGM"))
    .map(scenario => scenario.industryScenario.id);
    let scenarioString: string;
    if (this.scenarioSourceType == 1) {
      scenarioString = this.selectedGlobalScenario?.id;
    } else {
      scenarioString = idList.length > 1 ? idList.sort()[0] + "-" + idList.sort()[idList.length - 1] : idList[0]?.toString();
    }
    const fileName = 'IndustryConsolidation_' + scenarioString + "_" + currDate;

    this.industryScenarioService.getConsolidationReport(idList.join(", "), this.startYear, this.endYear,
      this.stateService.getUserGmid(), this.aggregateTos, fcorg).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting Industry Consolidation Report.', DISMISS_MSG);
        this.industrySpinner = false;
      }, () => {
        this.industrySpinner = false;
      }
    );
  }

  /**
   * Gets the propulsion consolidation report
   */
  getPropulsionConsolidationReport(): void {
    this.propulsionSpinner = true;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const idList = this.selectedScenarios.map(scenario => scenario.propulsionScenario.id);
    let scenarioString: string;
    if (this.scenarioSourceType == 1) {
      scenarioString = this.selectedGlobalScenario?.id;
    } else {
      scenarioString = idList.length > 1 ? idList.sort()[0] + "-" + idList.sort()[idList.length - 1] : idList[0]?.toString();
    }
    const fileName = 'PropulsionConsolidation_' + scenarioString + "_" + currDate;
    this.propulsionScenarioService.getConsolidationReport(this.selectedScenarios.map(scenario => scenario.vehScenId).join(", "),
      this.startYear, this.endYear, this.stateService.getUserGmid(), 'VolYrAcross', 0, 0).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting Propulsion Consolidation Report.', DISMISS_MSG);
        this.propulsionSpinner = false;
      }, () => {
        this.propulsionSpinner = false;
      }
    );
  }

  /**
   * Event handler for clicking a tvp scenario
   * @param event
   */
  onSelectTvp(event: any) {
    const row = event.data;
    if (event.node.selected) {
      this.selectedScenarios.push(row);
    } else {
      this.selectedScenarios = this.selectedScenarios.filter(element => element != row);
    }
    this.disableButtons();
  }

  /**
   * Sets the scenario as selected
   * @param data data from the event
   */
  onSelect(data: any) {
    this.selectedScenarios = data.selectedScenarios
    this.disableButtons();
  }

  /**
   * Checks states of scenarios and enables/disables buttons
   */
  disableButtons() {
    // Disable the Include Industry scenario checkbox if there is only non-GM scenarios selected
    this.disableIncludeIndustry = this.selectedScenarios.length > 0 &&
      this.selectedScenarios.map(scenario => scenario.fcorg).every(fcorg => fcorg.includes("NONGM"));
    if (this.disableIncludeIndustry) {
      this.includeIndustry = false;
    }
    // Disable the Include Propulsion Scenarios checkbox if there is more than one scenario from the same FCORG chosen
    if (hasDuplicates(this.selectedScenarios.map(scenario => scenario.fcorg))) {
      this.includePropulsion = false;
      this.disableIncludePropulsion = true;
    } else {
      this.disableIncludePropulsion = false;
    }
  }

  /**
   *  Event handler for when a global scenario is selected
   * @param event
   */
  selectGlobalScenario(event: any): void {
    this.selectedGlobalScenario = event.globalScenario;
    this.selectedScenarios = event.memberScenarios;
  }

  /**
   * Resets the selections and filters
   */
  resetSelections() {
    this.selectedFcorg = null;
    this.selectedScenarios = [];
    this.selectedGlobalScenario = null;
    this.resetCheckboxes();
    this.resetTVPFilters();
    this.setTables();
  }

  /**
   * Sets the table to display or hide hidden scenarios
   */
  setTables() {
    this.selectedScenarios = [];
    this.selectedGlobalScenario = null;

    if (this.scenarioSourceType === 2) {
      this.aggregateTos = false;
      this.includeLCA = true;
      this.displayedTvpScenarios = [...this.tvpScenarios];
      if (!this.showHiddenScenarios) {
        this.displayedTvpScenarios = this.tvpScenarios.filter(scenario => !scenario.isHidden);
      }
    }
  }

  /**
   * filters the tvp scenarios
   */
  getFilteredScenarioIds(): void {
    this.showSpinner = true;
    const forecastTypeIds = this.forecastTypes.filter(element => this.forecastTypeOptions.selectedOptions.includes(element.SCEN_TYPE)).map(element => element.SCEN_TYPE_ID);
    const forecastStatusIds = this.forecastStatus.filter(element => this.forecastStatusOptions.selectedOptions.includes(element.forecastState)).map(element => element.id);
    const propulsionTypeIds = this.propulsionTypes.filter(element => this.propulsionTypeOptions.selectedOptions.includes(element.propulsionType)).map(element => element.id);
    const engProgClassIds = this.engProgClasses.filter(element => this.engProgClassOptions.selectedOptions.includes(element.engProgClass)).map(element => element.id);
    const forecastGateIds = this.forecastGates.filter(element => this.forecastGateOptions.selectedOptions.includes(element.scenarioCategory)).map(element => element.scenarioCategoryId);
    const architectureIds = this.architectures.filter(element => this.architectureOptions.selectedOptions.includes(element.arch)).map(element => element.id);
    const marketDivisionIds = this.marketDivisions.filter(element => this.marketDivisionOptions.selectedOptions.includes(element.marketDivision)).map(element => element.id);
    const localBrandIds = this.localBrands.filter(element => this.localBrandOptions.selectedOptions.includes(element.localBrandValue)).map(element => element.id);
    const programCodeIds = this.programCodes.filter(element => this.programCodeOptions.selectedOptions.includes(element.prgmCode)).map(element => element.id);
    const forecastTypesString = forecastTypeIds.length > 0 ? forecastTypeIds.join(',') : 'all';
    const forecastStatusString = forecastStatusIds.length > 0 ? forecastStatusIds.join(',') : 'all';
    const propulsionTypesString = propulsionTypeIds.length > 0 ? propulsionTypeIds.join(',') : 'all';
    const engProgClassesString = engProgClassIds.length > 0 ? engProgClassIds.join(',') : 'all';
    const forecastGatesString = forecastGateIds.length > 0 ? forecastGateIds.join(',') : 'all';
    const architecturesString = architectureIds.length > 0 ? architectureIds.join(',') : 'all';
    const marketDivisionsString = marketDivisionIds.length > 0 ? marketDivisionIds.join(',') : 'all';
    const localBrandsString = localBrandIds.length > 0 ? localBrandIds.join(',') : 'all';
    const programCodesString = programCodeIds.length > 0 ? programCodeIds.join(',') : 'all';

    const requestDTO: FilterTvpScenarioRequestModel = {
      gmid: this.stateService.getUserGmid(),
      showHidden: this.showHiddenScenarios,
      forecastTypes: forecastTypesString,
      forecastStates: forecastStatusString,
      propTypes: propulsionTypesString,
      engProg: engProgClassesString,
      forecastGates: forecastGatesString,
      arch: architecturesString,
      mktDivs: marketDivisionsString,
      localBrands: localBrandsString,
      progCodes: programCodesString
    }
    this.vehicleScenarioService.getFilteredIDs(requestDTO).subscribe(
      (data) => {
        this.displayedTvpScenarios = this.tvpScenarios.filter(
          element => data.includes(element.vehScenId));
      },
      () => {
        this.snackBar.displayError('Error filtering TVP Scenarios', DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
      }
    );
  }


  /**
   * Resets all selected options for the filters
   */
  resetTVPFilters(): void {
    this.programCodeOptions.selectedOptions = [];
    this.localBrandOptions.selectedOptions = [];
    this.marketDivisionOptions.selectedOptions = [];
    this.architectureOptions.selectedOptions = [];
    this.forecastGateOptions.selectedOptions = [];
    this.forecastTypeOptions.selectedOptions = [];
    this.propulsionTypeOptions.selectedOptions = [];
    this.engProgClassOptions.selectedOptions = [];
    this.forecastStatusOptions.selectedOptions = [];
  }

  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const currentYear = new Date().getFullYear()
    // This report has a custom year range so that users can go back to an older year
    this.yearRange = range(1990, currentYear + 26);
    this.startYear = currentYear - 2;
    this.endYear = currentYear + 9;
  }

  /**
   * Resets all checkboxes to their default values
   */
  resetCheckboxes(): void {
    this.includeIndustry = true;
    this.includePropulsion = false;
    this.removeRecidNa = true;
    this.aggregateTos = true;
    this.includeZeroVolumes = true;
    this.includeLCA = false;
    this.showHiddenScenarios = false;
    this.disableIncludePropulsion = false;
    this.disableIncludeIndustry = false;
  }

}
